<template>
  <div class="attribute-manager">
    <div class="attr-toolbar">
      <el-button type="primary" size="small" icon="el-icon-plus" @click="showAddDialog">添加属性</el-button>
      <span class="attr-count">共 {{ attributes.length }} 个属性</span>
    </div>

    <div class="attr-list" v-loading="loading">
      <template v-if="groupedAttributes.length > 0">
        <div v-for="(group, gIdx) in groupedAttributes" :key="gIdx" class="attr-group">
          <div class="attr-group-header">
            <span class="attr-group-role">{{ group.role ? group.role.name : '未指定角色' }}</span>
            <el-tag size="mini" type="info">{{ group.attrs.length }} 个属性</el-tag>
          </div>
          <div class="attr-item" v-for="attr in group.attrs" :key="attr.id">
            <div class="attr-main">
              <span class="attr-name">{{ attr.name }}</span>
              <el-tag size="mini" type="info" effect="plain">初始值: {{ attr.initial_value }}</el-tag>
            </div>
            <div class="attr-actions">
              <el-button size="mini" icon="el-icon-edit" @click="editAttr(attr)">编辑</el-button>
              <el-button size="mini" type="danger" icon="el-icon-delete" @click="removeAttr(attr)">删除</el-button>
            </div>
          </div>
        </div>
      </template>
      <div v-if="!loading && attributes.length === 0" class="attr-empty">暂无属性</div>
    </div>

    <el-dialog :title="editingAttr && editingAttr.id ? '编辑属性' : '添加属性'" :visible.sync="dialogVisible" width="450px" append-to-body>
      <el-form v-if="editingAttr" :model="editingAttr" label-width="90px">
        <el-form-item label="角色">
          <el-select v-model="editingAttr.role_id" placeholder="选择角色" style="width: 100%;">
            <el-option v-for="role in roles" :key="role.id" :label="role.name" :value="role.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="属性名称">
          <el-input v-model="editingAttr.name" placeholder="如：好感度、智慧值" />
        </el-form-item>
        <el-form-item label="初始值">
          <el-input-number v-model="editingAttr.initial_value" style="width: 100%;" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveAttr">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { Game } from '@/api/game'
import { Role, getRoles } from '@/api/role'
import { GameAttribute, getGameAttributes, createGameAttribute, updateGameAttribute, deleteGameAttribute } from '@/api/gameAttribute'

@Component
export default class AttributeManager extends Vue {
  @Prop({ required: true }) readonly game!: Game

  attributes: GameAttribute[] = []
  roles: Role[] = []
  loading = false
  dialogVisible = false
  editingAttr: GameAttribute | null = null

  @Watch('game', { immediate: true })
  onGameChange(newGame: Game) {
    if (newGame && newGame.id) {
      this.loadData()
    }
  }

  async loadData() {
    this.loading = true
    try {
      const [attrs, roles] = await Promise.all([
        getGameAttributes(this.game.id),
        getRoles(this.game.id)
      ])
      this.attributes = attrs
      this.roles = roles
    } catch {
      this.$message.error('加载属性数据失败')
    } finally {
      this.loading = false
    }
  }

  get groupedAttributes(): Array<{ role: Role | null; attrs: GameAttribute[] }> {
    const groups: Map<number | undefined, GameAttribute[]> = new Map()
    for (const attr of this.attributes) {
      const key = attr.role_id
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(attr)
    }
    const result: Array<{ role: Role | null; attrs: GameAttribute[] }> = []
    for (const [roleId, attrs] of groups) {
      const role = roleId ? this.roles.find(r => r.id === roleId) || null : null
      result.push({ role, attrs })
    }
    return result
  }

  getRoleName(roleId: number): string {
    const role = this.roles.find(r => r.id === roleId)
    return role ? role.name : '角色' + roleId
  }

  showAddDialog() {
    this.editingAttr = {
      game_id: this.game.id,
      role_id: 0,
      name: '',
      initial_value: 0
    }
    this.dialogVisible = true
  }

  editAttr(attr: GameAttribute) {
    this.editingAttr = { ...attr }
    this.dialogVisible = true
  }

  async saveAttr() {
    if (!this.editingAttr) return
    if (!this.editingAttr.role_id) {
      this.$message.warning('请选择角色')
      return
    }
    if (!this.editingAttr.name) {
      this.$message.warning('请输入属性名称')
      return
    }
    try {
      if (this.editingAttr.id) {
        await updateGameAttribute(this.editingAttr.id, this.editingAttr)
      } else {
        await createGameAttribute(this.editingAttr)
      }
      this.$message.success('保存成功')
      this.dialogVisible = false
      this.loadData()
    } catch {
      this.$message.error('保存失败')
    }
  }

  async removeAttr(attr: GameAttribute) {
    if (!attr.id) return
    try {
      await this.$confirm('确定删除该属性？', '提示', { type: 'warning' })
      await deleteGameAttribute(attr.id)
      this.$message.success('删除成功')
      this.loadData()
    } catch (e) {
      if (e !== 'cancel') this.$message.error('删除失败')
    }
  }
}
</script>

<style scoped>
.attr-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.attr-count {
  font-size: 13px;
  color: #909399;
}

.attr-list {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  max-height: 60vh;
  overflow-y: auto;
}

.attr-empty {
  text-align: center;
  color: #909399;
  padding: 40px 0;
  font-size: 14px;
}

.attr-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f2f3f5;
  transition: background 0.15s;
}

.attr-item:last-child {
  border-bottom: none;
}

.attr-item:hover {
  background: #f9fafb;
}

.attr-main {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.attr-role {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.attr-name {
  font-size: 14px;
  color: #606266;
}

.attr-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.attr-group {
  margin-bottom: 16px;
}

.attr-group:last-child {
  margin-bottom: 0;
}

.attr-group-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 8px;
}

.attr-group-role {
  font-weight: 600;
  font-size: 14px;
  color: #303133;
}
</style>
